/**
 * Property Requests Service
 *
 * Handles API calls for individual property requests.
 * Used by individuals who want to list their properties through Safaqat 24's FAL license.
 */

import { apiRequest } from '../utils/apiClient';

export type RequestStatus = 'draft' | 'pending_review' | 'approved' | 'rejected' | 'listed' | 'completed';

export interface PropertyRequestLocation {
  city: string;
  area?: string;
  address?: string;
  addressAr?: string;
}

export interface PropertyRequest {
  id: string;
  userId: string;

  // Owner details
  ownerName: string;
  ownerNameAr?: string;
  ownerPhone: string;
  ownerEmail?: string;

  // Property details
  propertyType: string;
  listingType: 'SALE' | 'RENT';
  title: string;
  titleAr?: string;
  description?: string;
  descriptionAr?: string;

  // Location
  cityId?: string;
  areaId?: string;
  address?: string;
  addressAr?: string;

  // Specifications
  bedrooms?: number;
  bathrooms?: number;
  sqft?: number;
  price?: number;
  priceNegotiable?: boolean;

  // Status tracking
  status: RequestStatus;
  rejectionReason?: string;
  reviewedBy?: string;
  reviewedAt?: string;

  // Linked property (once listed)
  propertyId?: string;

  createdAt: string;
  updatedAt: string;
}

export interface PropertyRequestsResponse {
  data: PropertyRequest[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
  };
}

export interface PropertyRequestParams {
  page?: number;
  pageSize?: number;
  status?: RequestStatus;
}

const propertyRequestsService = {
  /**
   * Get current user's property requests.
   */
  async getMyRequests(
    params?: PropertyRequestParams,
    token?: string
  ): Promise<PropertyRequestsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));
    if (params?.status) queryParams.set('status', params.status);

    const queryString = queryParams.toString();
    const path = `/registration/individual-requests${queryString ? `?${queryString}` : ''}`;

    const response = await apiRequest<{ data: PropertyRequest[]; meta?: PropertyRequestsResponse['meta'] }>(
      path,
      { method: 'GET', token }
    );

    return {
      data: response.data,
      meta: response.meta || {
        total: response.data.length,
        page: params?.page || 1,
        pageSize: params?.pageSize || 20,
      },
    };
  },

  /**
   * Get a single request by ID.
   */
  async getRequest(id: string, token?: string): Promise<PropertyRequest> {
    const response = await apiRequest<{ data: PropertyRequest }>(
      `/registration/individual-requests/${id}`,
      { method: 'GET', token }
    );
    return response.data;
  },

  /**
   * Cancel a pending request.
   */
  async cancelRequest(id: string, token?: string): Promise<void> {
    await apiRequest<void>(
      `/registration/individual-requests/${id}/cancel`,
      { method: 'POST', token }
    );
  },

  /**
   * Promote a draft request to pending_review.
   *
   * Used as a manual recovery path if the create-then-submit chain in
   * registrationService failed to complete the second step (e.g. a draft
   * created before the consent fields were sent on create, or a network
   * blip between the two calls).
   *
   * The `/submit` endpoint rejects with 400 unless `authorizationConsent`
   * and `agreedToTerms` are both already true on the row. Since the user
   * had to accept the FAL disclaimer page to even reach this UI, we PUT
   * those flags before submitting — making the rescue path idempotent
   * regardless of how the draft was created.
   */
  async submitRequest(id: string, token?: string): Promise<PropertyRequest> {
    try {
      await apiRequest<{ data: PropertyRequest }>(
        `/registration/individual-requests/${id}`,
        {
          method: 'PUT',
          body: {
            authorizationConsent: true,
            agreedToTerms: true,
          },
          token,
        }
      );
    } catch (e) {
      // Pre-flight failure is rare and recoverable from the next attempt.
      // We still try to submit — if consents truly were missing, the submit
      // call will surface the original 400 to the caller.
      console.warn('Could not pre-set consents before submitting; trying anyway.', e);
    }

    const response = await apiRequest<{ data: PropertyRequest }>(
      `/registration/individual-requests/${id}/submit`,
      { method: 'POST', token }
    );
    return response.data;
  },
};

export default propertyRequestsService;
